import { Trans } from "@lingui/react/macro";
import {
  informationFieldConfigValueSchema,
  patientKeys,
  updateAddress,
} from "@/api/Patients";
import Form from "@/components/Form/Form";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deducedError } from "@/Utils/ErrorUtils";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import {
  formatAddress,
  type IAddressWithCoordinates,
} from "@medoma/models/addresses";
import TextArea from "@/components/TextArea/TextArea";
import { useLingui } from "@lingui/react/macro";
import { conventionalPostalCodeSchema } from "@medoma/models/postalCode";
import { useUserProfile } from "@/api/Users";
import type z from "zod";

export const EditAdditionalAddressInformation = ({
  currentAddress,
  onSuccess,
  patientId,
  fieldConfig,
}: {
  currentAddress: IAddressWithCoordinates;
  onSuccess: () => void;
  patientId: string;
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    setError,
    reset,
  } = useForm<IAddressWithCoordinates>({
    defaultValues: {
      additionalInformation: currentAddress.additionalInformation,
    },
  });

  const queryClient = useQueryClient();
  const { countryCode } = useUserProfile();

  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (address: IAddressWithCoordinates) =>
      updateAddress(patientId, address),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  const validateAndSubmit = handleSubmit((formData) => {
    const postalCode = conventionalPostalCodeSchema.shape[countryCode].parse(
      currentAddress.postalCode,
    );

    return mutate(
      formatAddress({
        ...currentAddress,
        additionalInformation: formData.additionalInformation,
        countryCode,
        postalCode,
      }),
    );
  });

  const { t } = useLingui();

  return (
    <Form onSubmit={validateAndSubmit}>
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar övrig adressinformation`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : null}
          <TextArea
            label={t`Övrig adressinformation`}
            showOptionalLabel={
              fieldConfig === informationFieldConfigValueSchema.enum.optional
            }
            {...register("additionalInformation", {
              required: {
                value:
                  fieldConfig ===
                  informationFieldConfigValueSchema.enum.required,
                message: t`Ange övrig adressinformation`,
              },
            })}
            formatHint={t`T.ex portkod, parkering`}
            errorMessage={errors.additionalInformation?.message}
          />

          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};
